import React from "react";
import { Button, Tag } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

interface ChequeCalendarProps {
  calendarDate: dayjs.Dayjs;
  onDateChange: (d: dayjs.Dayjs) => void;
  cheques: any[];
  onDayClick: (dateStr: string) => void;
  onChequeClick: (cheque: any) => void;
}

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const statusColor: Record<string, string> = {
  pending: "warning",
  cleared: "success",
  bounced: "error",
  cancelled: "default",
};

export const ChequeCalendar: React.FC<ChequeCalendarProps> = ({
  calendarDate,
  onDateChange,
  cheques,
  onDayClick,
  onChequeClick,
}) => {
  const startOfMonth = calendarDate.startOf("month");
  const endOfMonth = calendarDate.endOf("month");
  const startDayOfWeek = startOfMonth.day();
  const totalDays = endOfMonth.date();

  const days: dayjs.Dayjs[] = [];

  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    days.push(startOfMonth.subtract(i + 1, "day"));
  }
  for (let i = 1; i <= totalDays; i++) {
    days.push(startOfMonth.date(i));
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(endOfMonth.add(i, "day"));
  }

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1">
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={() => onDateChange(calendarDate.subtract(1, "month"))}
          />
          <Button size="small" onClick={() => onDateChange(dayjs())}>
            Today
          </Button>
          <Button
            size="small"
            icon={<RightOutlined />}
            onClick={() => onDateChange(calendarDate.add(1, "month"))}
          />
        </div>
        <span className="text-sm font-semibold text-gray-800">
          {calendarDate.format("MMMM YYYY")}
        </span>
        <span className="text-xs text-gray-400">
          Click a day to add a cheque
        </span>
      </div>

      <div className="grid grid-cols-7 border-b border-gray-200 text-center bg-gray-50">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="py-2 text-xs font-medium text-gray-500">
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 divide-x divide-y divide-gray-100">
        {days.map((day, idx) => {
          const dayStr = day.format("YYYY-MM-DD");
          const isCurrentMonth = day.month() === calendarDate.month();
          const isToday = day.isSame(dayjs(), "day");
          const dayCheques = cheques.filter((c) => c.cheque_date === dayStr);

          return (
            <div
              key={idx}
              className={`min-h-[100px] p-1.5 flex flex-col gap-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                isCurrentMonth ? "bg-white" : "bg-gray-50"
              }`}
              onClick={() => onDayClick(dayStr)}
            >
              <span
                className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full self-start ${
                  isToday
                    ? "bg-blue-600 text-white"
                    : isCurrentMonth
                      ? "text-gray-800"
                      : "text-gray-400"
                }`}
              >
                {day.date()}
              </span>

              {dayCheques.slice(0, 3).map((c) => (
                <div
                  key={c.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChequeClick(c);
                  }}
                  className="text-[10px] truncate px-1 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 cursor-pointer"
                  title={`${c.cheque_no} — LKR ${parseFloat(c.amount).toLocaleString()}`}
                >
                  <Tag
                    color={statusColor[c.status_code] || "default"}
                    className="text-[9px] m-0 mr-1 px-1 py-0 leading-tight"
                  >
                    {c.type === "in" ? "IN" : "OUT"}
                  </Tag>
                  {c.cheque_no}
                </div>
              ))}

              {dayCheques.length > 3 && (
                <span className="text-[10px] text-gray-400">
                  +{dayCheques.length - 3} more
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
